/* Site-wide sticky controls for rewards and support. */
if (!customElements.get('sticky-cart-bar')) {
  class StickyCartBar extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.updateBodyOffset = this.updateBodyOffset.bind(this);
      this.updateCartDrawerState = this.updateCartDrawerState.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateBodyOffset);
        this.resizeObserver.observe(this);
      }
      window.addEventListener('resize', this.updateBodyOffset);

      this.cartDrawerObserver = new MutationObserver(() => {
        // Re-scope to the drawer's open attribute as soon as it exists — the
        // broad insertion-watch below otherwise keeps firing on page-wide
        // DOM churn (and open toggles on unrelated <details>).
        const drawer = document.getElementById('cart-drawer');
        if (drawer && this.cartDrawerObserverTarget !== drawer) {
          this.cartDrawerObserver.disconnect();
          this.cartDrawerObserverTarget = drawer;
          this.cartDrawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
        }
        this.updateCartDrawerState();
      });
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer) {
        this.cartDrawerObserverTarget = cartDrawer;
        this.cartDrawerObserver.observe(cartDrawer, {
          attributes: true,
          attributeFilter: ['open']
        });
      } else if (window.Shopify && window.Shopify.designMode) {
        // The drawer section renders before this bar, so if it's absent now it
        // can only appear later via theme-editor injection — keep the broad
        // insertion-watch for the editor only. Storefront pages without a
        // drawer (e.g. cart type "page") get no document-wide observer at all.
        this.cartDrawerObserver.observe(document.documentElement, {
          childList: true,
          subtree: true
        });
      }

      this.updateCartDrawerState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.updateBodyOffset);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.cartDrawerObserver) this.cartDrawerObserver.disconnect();
      document.body.classList.remove('has-sticky-cart-bar');
      document.body.classList.remove('has-cart-drawer-open');
      document.body.style.removeProperty('--sticky-cart-bar-offset');
    }

    handleClick(evt) {
      const rewardsButton = evt.target.closest('.sticky-cart-bar__gift');
      if (rewardsButton) {
        evt.preventDefault();
        this.closeCartDrawer();
        this.closeChat();
        this.openRewards();
        return;
      }

      const supportButton = evt.target.closest('.sticky-cart-bar__support');
      if (!supportButton) return;

      evt.preventDefault();
      this.closeCartDrawer();
      this.closeRewards();
      if (window.svOpenSupport) {
        window.svOpenSupport();
      }
    }

    closeCartDrawer() {
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && cartDrawer.hasAttribute('open')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
      }
    }

    closeRewards() {
      if (window.SmileUI && typeof window.SmileUI.closePanel === 'function') {
        try {
          window.SmileUI.closePanel();
        } catch (error) {
          console.warn('[sticky-cart-bar] SmileUI.closePanel() failed', error);
        }
      }
    }

    closeChat() {
      if (window.svCloseSupport) {
        try {
          window.svCloseSupport();
        } catch (error) {
          console.warn('[sticky-cart-bar] closing support failed', error);
        }
      }
    }

    barApplies() {
      return (
        window.getComputedStyle(this).display !== 'none' &&
        !this.classList.contains('sticky-cart-bar--hidden') &&
        !this.classList.contains('sticky-cart-bar--suppressed')
      );
    }

    updateSuppressed() {
      const cartDrawer = document.getElementById('cart-drawer');
      const drawerOpen = Boolean(cartDrawer && cartDrawer.hasAttribute('open'));
      this.classList.toggle('sticky-cart-bar--suppressed', drawerOpen);
      this.updateBodyOffset();
    }

    updateBodyOffset() {
      const isVisible = this.barApplies();

      document.body.classList.toggle('has-sticky-cart-bar', isVisible);
      if (isVisible) {
        document.body.style.setProperty(
          '--sticky-cart-bar-offset',
          Math.ceil(window.innerHeight - this.getBoundingClientRect().top) + 'px'
        );
      } else {
        document.body.style.removeProperty('--sticky-cart-bar-offset');
      }
    }

    updateCartDrawerState() {
      const cartDrawer = document.getElementById('cart-drawer');
      document.body.classList.toggle(
        'has-cart-drawer-open',
        Boolean(cartDrawer && cartDrawer.hasAttribute('open'))
      );
      this.updateSuppressed();
    }

    openRewards() {
      if (window.SmileUI && typeof window.SmileUI.openPanel === 'function') {
        try {
          window.SmileUI.openPanel();
          return;
        } catch (error) {
          console.warn('[sticky-cart-bar] SmileUI.openPanel() failed', error);
        }
      }
      window.location.hash = 'smile-home';
    }
  }

  customElements.define('sticky-cart-bar', StickyCartBar);
}
